import React, { useEffect, useRef, useState } from "react";
import styles from "./LegalPage.module.css";

// Shared shell for the legal documents (/privacy, /terms). The login screen
// is a dark glass pane you look AT; a policy is a document you look INTO ,
// so the navy world stays as the shell and the text sits on a light paper
// sheet, with a numbered table of contents on the navy beside it.
//
// sections: [{ id, heading, body }] , body is JSX. The TOC numbers mirror
// the document's real section order.
const LegalPage = ({ title, updatedNote, sections, contact }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const sectionRefs = useRef({});

  useEffect(() => {
    document.title = `${title} | TOCA Health`;
    // Paint the page background navy while mounted so the light body
    // background can never show around the document (the root spans its
    // container, the body covers everything beyond it).
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#0d0e2e";
    return () => {
      document.title = "Toca Health";
      document.body.style.backgroundColor = prevBg;
    };
  }, [title]);

  // Track which section is in view so the TOC follows the reader.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.dataset.sectionId);
      },
      { rootMargin: "-15% 0px -70% 0px" }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  const scrollToSection = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className={styles.root}>
      <header className={styles.topBar}>
        <a href="/login" className={styles.brand} aria-label="TOCA Health">
          <img
            src="/assets/images/th_logo.svg"
            alt="TOCA Health"
            className={styles.brandLogo}
          />
        </a>
        <a href="/login" className={styles.backLink}>
          Back to sign in
        </a>
      </header>

      <div className={styles.layout}>
        <nav className={styles.toc} aria-label="On this page">
          <p className={styles.tocLabel}>On this page</p>
          <ol className={styles.tocList}>
            {sections.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={`${styles.tocItem} ${
                    activeId === s.id ? styles.tocItemActive : ""
                  }`}
                  onClick={() => scrollToSection(s.id)}
                >
                  <span className={styles.tocNum}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {s.heading}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <main className={styles.sheet}>
          <p className={styles.eyebrow}>TOCA Health</p>
          <h1 className={styles.title}>
            {title}
            <span className={styles.cursor}>_</span>
          </h1>
          {updatedNote && <p className={styles.updated}>{updatedNote}</p>}

          {sections.map((s) => (
            <section
              key={s.id}
              data-section-id={s.id}
              ref={(el) => {
                sectionRefs.current[s.id] = el;
              }}
              className={styles.section}
            >
              <h2 className={styles.heading}>{s.heading}</h2>
              {s.body}
            </section>
          ))}

          {contact && (
            <footer className={styles.contact}>
              <p className={styles.contactLabel}>Contact</p>
              {contact}
            </footer>
          )}
        </main>
      </div>
    </div>
  );
};

export default LegalPage;
